import L from 'leaflet';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-leaflet';

// Reuse these icon objects so React-Leaflet does not replace a marker while it is being dragged.
const errorIcon = new L.DivIcon({
  className: 'custom-error-icon',
  html: '<div style="background-color: red; color: white; border-radius: 50%; width: 25px; height: 25px; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.3);">!</div>',
  iconSize: [25, 25],
  iconAnchor: [12, 25],
});

const defaultMarkerIcon = new L.Icon.Default();

/**
 * Waits until the browser is ready to draw the next screen before showing a drag preview.
 * The timer fallback keeps the helper usable when animation frames are not available.
 */
const requestDragFrame = (callback) =>
  globalThis.requestAnimationFrame?.(callback) ?? globalThis.setTimeout(callback, 16);

/** Cancels work created by {@link requestDragFrame}. */
const cancelDragFrame = (frameId) => {
  if (globalThis.cancelAnimationFrame) {
    globalThis.cancelAnimationFrame(frameId);
    return;
  }

  globalThis.clearTimeout(frameId);
};

/** Copies a Leaflet marker position into the plain coordinate shape used by the app. */
const getMarkerPosition = (event) => {
  const position = event?.target?.getLatLng?.();

  return position ? { lat: position.lat, lng: position.lng } : null;
};

/**
 * Renders a map marker that supports a smooth preview while dragging and one final update when
 * released. A caller may also limit where the marker can move, which is used to keep square resize
 * handles attached to their corners.
 *
 * @param {Object} props
 * @param {{lat: number, lng: number}} props.position Current saved marker position.
 * @param {Function} [props.onMarkerDrag] Receives temporary positions while the marker moves.
 * @param {Function} props.onMarkerDragEnd Receives the final position when the marker is released.
 * @param {Function} [props.getConstrainedMarkerPosition] Adjusts a dragged position when needed.
 * @param {number} [props.markerIndex] Identifies this marker to the caller.
 * @param {boolean} [props.showErrorIcon] Shows the shared warning marker instead of the normal icon.
 * @param {Object} [props.icon] Optional Leaflet icon for special handles.
 * @param {string} [props.accessibleName] Describes the marker to assistive technology.
 * @param {boolean} [props.keyboard] Allows Leaflet keyboard focus when true.
 * @param {boolean} [props.riseOnHover] Raises the marker above nearby markers on hover.
 * @param {number} [props.zIndexOffset] Changes the marker's stacking order.
 * @param {*} [props.children] Optional popup or other marker content.
 * @returns {JSX.Element|null}
 */
const DraggableMarker = ({
  position,
  onMarkerDrag,
  onMarkerDragEnd,
  getConstrainedMarkerPosition,
  markerIndex,
  showErrorIcon,
  icon,
  accessibleName = 'Draggable map marker',
  keyboard = true,
  riseOnHover = true,
  zIndexOffset = 0,
  onClick,
  draggable = true,
  children,
}) => {
  const onMarkerDragRef = useRef(onMarkerDrag);
  const onMarkerDragEndRef = useRef(onMarkerDragEnd);
  const getConstrainedMarkerPositionRef = useRef(getConstrainedMarkerPosition);
  const pendingDragPosition = useRef(null);
  const dragFrame = useRef(null);
  const isDragging = useRef(false);
  const dragStartPosition = useRef(position);

  // Keep the newest callbacks available without rebuilding Leaflet's event handlers on every draw.
  onMarkerDragRef.current = onMarkerDrag;
  onMarkerDragEndRef.current = onMarkerDragEnd;
  getConstrainedMarkerPositionRef.current = getConstrainedMarkerPosition;

  // Outside a drag, remember the latest saved position as the starting point for the next drag.
  if (!isDragging.current) dragStartPosition.current = position;

  /** Stops a waiting preview so it cannot run after the drag ends or the marker is removed. */
  const cancelPendingDrag = useCallback(() => {
    if (dragFrame.current !== null) {
      cancelDragFrame(dragFrame.current);
      dragFrame.current = null;
    }
    pendingDragPosition.current = null;
  }, []);

  const scheduleDragPreview = useCallback(
    (nextPosition) => {
      // Keep replacing this value so each screen uses only the newest position from Leaflet.
      pendingDragPosition.current = nextPosition;
      if (dragFrame.current !== null) return;

      dragFrame.current = requestDragFrame(() => {
        dragFrame.current = null;
        const previewPosition = pendingDragPosition.current;
        pendingDragPosition.current = null;
        if (previewPosition) onMarkerDragRef.current?.(markerIndex, previewPosition);
      });
    },
    [markerIndex],
  );

  const getDragPosition = useCallback(
    (event) => {
      const markerPosition = getMarkerPosition(event);
      if (!markerPosition || !getConstrainedMarkerPositionRef.current) return markerPosition;

      // A constraint may move the raw pointer position back to a valid location, such as a
      // generated square corner.
      const constrainedPosition = getConstrainedMarkerPositionRef.current(
        markerIndex,
        markerPosition,
      );
      if (
        !Number.isFinite(Number(constrainedPosition?.lat)) ||
        !Number.isFinite(Number(constrainedPosition?.lng))
      ) {
        return markerPosition;
      }

      const normalizedPosition = {
        lat: Number(constrainedPosition.lat),
        lng: Number(constrainedPosition.lng),
      };

      // Move Leaflet immediately as well as returning the value, so the handle stays under the
      // pointer instead of briefly showing the invalid raw position.
      event?.target?.setLatLng?.(normalizedPosition);
      return normalizedPosition;
    },
    [markerIndex],
  );

  useEffect(() => () => cancelPendingDrag(), [cancelPendingDrag]);

  // Marker clicks stay on the marker. Movement is previewed during drag and saved on release.
  const eventHandlers = useMemo(
    () => ({
      mousedown: (event) => {
        if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
      },
      click: (event) => {
        if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
        onClick?.(markerIndex, event);
      },
      dragstart: () => {
        cancelPendingDrag();
        isDragging.current = true;
      },
      drag: (event) => {
        if (!onMarkerDragRef.current && !getConstrainedMarkerPositionRef.current) return;

        const nextPosition = getDragPosition(event);
        if (nextPosition && onMarkerDragRef.current) scheduleDragPreview(nextPosition);
      },
      dragend: (event) => {
        if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
        cancelPendingDrag();
        isDragging.current = false;

        const finalPosition = getDragPosition(event);
        if (!finalPosition) return;

        const wasAccepted = onMarkerDragEndRef.current?.(markerIndex, finalPosition);
        if (wasAccepted === false) {
          // Leaflet owns the marker position while it is dragged. When a caller rejects a move
          // (for example, because it creates a bowtie or overlapping polygon edge), React's saved
          // position has not changed and therefore cannot trigger Leaflet to move back on its own.
          event?.target?.setLatLng?.(dragStartPosition.current);
        }
      },
    }),
    [cancelPendingDrag, getDragPosition, markerIndex, onClick, scheduleDragPreview],
  );

  if (!position) return null;

  // Leaflet moves the marker during the drag. Keeping React on the start value prevents a saved
  // position from pulling the marker backward before the final update arrives.
  const renderedPosition = isDragging.current ? dragStartPosition.current : position;

  return (
    <Marker
      position={renderedPosition}
      draggable={draggable}
      eventHandlers={eventHandlers}
      icon={showErrorIcon ? errorIcon : (icon ?? defaultMarkerIcon)}
      title={accessibleName}
      alt={accessibleName}
      keyboard={keyboard}
      autoPanOnFocus={keyboard}
      riseOnHover={riseOnHover}
      zIndexOffset={zIndexOffset}
    >
      {children}
    </Marker>
  );
};

export default DraggableMarker;
